// firebaseApi.web.ts
import {
  addDoc,
  collection,
  type DocumentData,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  type QueryConstraint,
  type QuerySnapshot,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebaseInit";
import type { FirestoreApi } from "./firestoreApi.interface";

export const firestoreApi: FirestoreApi = {
  addDocument: async (collectionName: string, data: DocumentData) => {
    const collectionRef = collection(db, collectionName);
    return await addDoc(collectionRef, data);
  },

  getDocument: async (collectionName: string, docId: string) => {
    const docRef = doc(db, collectionName, docId);
    return await getDoc(docRef);
  },

  setDocument: async (
    collectionName: string,
    docId: string,
    data: DocumentData,
  ) => {
    const docRef = doc(db, collectionName, docId);
    return await setDoc(docRef, data);
  },

  updateDocument: async (
    collectionName: string,
    docId: string,
    data: DocumentData,
  ) => {
    const docRef = doc(db, collectionName, docId);
    return await updateDoc(docRef, data);
  },

  deleteDocument: async (collectionName: string, docId: string) => {
    const docRef = doc(db, collectionName, docId);
    return await deleteDoc(docRef);
  },

  subscribeToDocument: (
    collectionName: string,
    docId: string,
    callback: (doc: DocumentData | undefined) => void,
  ) => {
    const docRef = doc(db, collectionName, docId);
    return onSnapshot(docRef, (snapshot) => {
      callback(snapshot);
    });
  },

  subscribeToQuery: (
    collectionName: string,
    queryConstraints: QueryConstraint[],
    callback: (docs: QuerySnapshot<DocumentData, DocumentData>) => void,
  ) => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...queryConstraints);

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      callback(querySnapshot);
    });

    // Return the unsubscribe function to allow unsubscribing from the listener
    return unsubscribe;
  },

  queryDocuments: async (
    collectionName: string,
    ...queryConstraints: QueryConstraint[]
  ) => {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs;
  },
};
